import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ChevronLeft, Sparkles } from "lucide-react";
import { api, fileUrl, fmtEur, getErrorMessage } from "../../api";
import { useCart } from "../../CartContext";
import { Badge } from "../../components/ui/badge";
import PropagationBadge from "../../components/PropagationBadge";
import FloatingCartSummary from "../../components/FloatingCartSummary";
import { Minus, Plus, BellRing } from "lucide-react";
import { toast } from "sonner";

export default function ProductDetail() {
  const { id } = useParams();
  const { cart, setItem } = useCart();
  const [p, setP] = useState(null);
  const [activePhoto, setActivePhoto] = useState(0);

  useEffect(() => {
    setP(null); setActivePhoto(0);
    api.get(`/catalog/${id}`).then((r) => setP(r.data)).catch(() => setP({ notFound: true }));
  }, [id]);

  if (!p) return <div className="max-w-5xl mx-auto p-6 text-[#5C665A]" data-testid="product-detail-loading">Loading…</div>;
  if (p.notFound) return <div className="max-w-5xl mx-auto p-6 text-[#5C665A]" data-testid="product-detail-notfound">Product not found.</div>;

  const qtyBySv = Object.fromEntries((cart?.items || []).map((i) => [i.size_variant_id, i.quantity]));

  const linkParent = (parent) => {
    if (!parent) return <span className="text-[#5C665A]">—</span>;
    if (parent.linked_product_id) return <Link to={`/catalog/${parent.linked_product_id}`} className="underline underline-offset-2 hover:text-[#1B4332]" data-testid={`pd-lineage-link-${parent.linked_product_id}`}>{parent.name}</Link>;
    return <span>{parent.name}</span>;
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6" data-testid="product-detail">
      <Link to="/catalog" className="text-sm text-[#5C665A] hover:text-[#1A1D1A] inline-flex items-center gap-1 mb-3" data-testid="pd-back">
        <ChevronLeft size={14} /> Back to catalog
      </Link>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Photos */}
        <div>
          <div className="aspect-square bg-[#F9F9F8] rounded-lg border border-[#E2E4E0] overflow-hidden">
            {p.photos?.[activePhoto] ? (
              <img src={fileUrl(p.photos[activePhoto].storage_path)} alt={p.name} className="w-full h-full object-contain" data-testid="pd-main-image" />
            ) : (
              <div className="w-full h-full grid place-items-center text-[#5C665A] text-sm">No photo</div>
            )}
          </div>
          {p.photos?.length > 1 && (
            <div className="mt-2 flex gap-2 overflow-x-auto">
              {p.photos.map((ph, i) => (
                <button key={i} onClick={() => setActivePhoto(i)} className={`w-16 h-16 rounded-md overflow-hidden border-2 flex-shrink-0 ${i === activePhoto ? "border-[#2D6A4F]" : "border-transparent"}`} data-testid={`pd-thumb-${i}`}>
                  <img src={fileUrl(ph.storage_path)} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Details */}
        <div>
          <div className="flex items-start justify-between gap-2 flex-wrap">
            <h1 className="db-h text-2xl sm:text-3xl font-bold tracking-tight break-words min-w-0 flex-1" data-testid="pd-name">{p.name}</h1>
            <div className="flex items-center gap-1.5 shrink-0 flex-wrap">
              {p.is_own_cross && (
                <Badge className="bg-[#E9F5EC] text-[#1B4332] border-transparent inline-flex items-center gap-1"><Sparkles size={12} /> DocBlock Cross</Badge>
              )}
              <PropagationBadge type={p.propagation_type} testid="pd-propagation" />
            </div>
          </div>
          {(p.seed_parent || p.pollen_parent) && (
            <div className="text-sm text-[#5C665A] mt-1" data-testid="pd-lineage">
              {linkParent(p.seed_parent)} <span className="opacity-60">×</span> {linkParent(p.pollen_parent)}
            </div>
          )}
          {p.description && <p className="text-sm text-[#1A1D1A] mt-4 whitespace-pre-line" data-testid="pd-description">{p.description}</p>}

          <div className="mt-5 divide-y divide-[#E2E4E0] border-y border-[#E2E4E0]">
            {p.sizes.map((s) => {
              const cartItem = (cart?.items || []).find((i) => i.size_variant_id === s.size_variant_id);
              return (
                <SizeRow
                  key={s.size_variant_id} size={s}
                  qty={qtyBySv[s.size_variant_id] || 0}
                  moreRequested={!!cartItem?.more_requested}
                  onSet={(q, more) => setItem(s.size_variant_id, q, { more_requested: more }).catch((e) => toast.error(getErrorMessage(e, "Failed")))}
                />
              );
            })}
          </div>
        </div>
      </div>
      <FloatingCartSummary />
    </div>
  );
}

function priceForQty(breaks, qty) {
  if (!qty || qty < 1) qty = 1;
  const hit = breaks.find((b) => qty >= b.min_qty && (b.max_qty == null || qty <= b.max_qty));
  return hit ? hit.unit_price : breaks[0]?.unit_price ?? 0;
}

function SizeRow({ size, qty, moreRequested = false, onSet }) {
  const active = priceForQty(size.tier_breaks, qty || 1);
  const [askMore, setAskMore] = useState(false);

  const bandedCap = size.display_qty ?? null;
  const exactCap = size.stock_qty ?? null;
  const cap = exactCap ?? bandedCap ?? Number.POSITIVE_INFINITY;
  const isBanded = bandedCap != null && exactCap == null;
  const atCap = isBanded && qty >= bandedCap && !moreRequested;

  const isIn = size.in_stock ?? (size.stock_qty ?? 0) > 0;

  const setQ = (q) => {
    if (moreRequested && isBanded) { onSet(Math.max(0, q), q > bandedCap); return; }
    onSet(Math.max(0, Math.min(cap, q)), false);
  };

  return (
    <div className="py-3 sm:py-4" data-testid={`pd-size-${size.size_variant_id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-base font-medium">
            {size.size_name}
            {size.size_note && <span className="ml-1 text-[12px] text-[#5C665A]/80 font-normal">({size.size_note})</span>}
          </div>
          <div className="text-[11px] db-mono text-[#5C665A] break-all">{size.sku}</div>
        </div>
        <div className="text-right shrink-0">
          <div className="db-mono text-lg sm:text-xl font-bold" data-testid={`pd-price-${size.size_variant_id}`}>{fmtEur(active)}</div>
          {size.is_partner_priced && (
            <div className="text-[10px] uppercase tracking-wider font-semibold text-[#8A5A2B]" data-testid={`pd-partner-label-${size.size_variant_id}`}>Partner price</div>
          )}
          {(() => {
            if (!isIn) return <div className="text-xs text-[#C94A4A] db-mono">sold out</div>;
            if (size.stock_qty != null && size.stock_qty <= 5) return <div className="text-xs text-[#D4A373] db-mono">only {size.stock_qty} left</div>;
            if (size.stock_qty != null) return <div className="text-xs text-[#5C665A] db-mono">{size.stock_qty} in stock</div>;
            if (size.display_qty != null) return <div className="text-xs text-[#5C665A] db-mono">{size.display_qty}+ available</div>;
            return <div className="text-xs text-[#5C665A] db-mono">in stock</div>;
          })()}
        </div>
      </div>
      {!size.is_partner_priced && (
        <div className="text-xs db-mono text-[#5C665A] mt-1 break-words">
          {size.tier_breaks.map((b, i) => (
            <span key={i}>{b.min_qty}{b.max_qty ? `–${b.max_qty}` : "+"}: {fmtEur(b.unit_price)}{i < size.tier_breaks.length - 1 ? " · " : ""}</span>
          ))}
        </div>
      )}
      <div className="mt-2 flex items-center justify-end gap-1 flex-wrap">
        {!isIn ? <NotifyBtn sizeVariantId={size.size_variant_id} /> : (
          <>
            <button className="w-9 h-9 rounded-md border border-[#E2E4E0] grid place-items-center hover:bg-[#F9F9F8] disabled:opacity-40" onClick={() => setQ(qty - 1)} disabled={qty <= 0} data-testid={`pd-dec-${size.size_variant_id}`}><Minus size={14} /></button>
            <input type="number" min={0} value={qty} onChange={(e) => setQ(Number(e.target.value) || 0)} className="db-inline w-14 h-9 text-center" data-testid={`pd-qty-${size.size_variant_id}`} />
            <button
              className="w-9 h-9 rounded-md border border-[#E2E4E0] grid place-items-center hover:bg-[#F9F9F8] disabled:opacity-40"
              onClick={() => { if (atCap) { setAskMore(true); return; } setQ(qty + 1); }}
              disabled={!atCap && qty >= cap}
              data-testid={`pd-inc-${size.size_variant_id}`}
            ><Plus size={14} /></button>
            {atCap && (
              <button
                type="button"
                onClick={() => setAskMore(true)}
                className="ml-2 text-xs underline text-[#2D6A4F] hover:text-[#1B4332]"
                data-testid={`pd-more-${size.size_variant_id}`}
              >
                Looking for more?
              </button>
            )}
            {moreRequested && (
              <span className="ml-2 text-xs text-[#5C665A] db-mono" data-testid={`pd-requested-${size.size_variant_id}`}>
                requested
              </span>
            )}
          </>
        )}
      </div>
      {askMore && (
        <MoreDialog
          size={size}
          current={qty}
          onCancel={() => setAskMore(false)}
          onSubmit={(desired) => { setAskMore(false); onSet(desired, true); }}
        />
      )}
    </div>
  );
}

function MoreDialog({ size, current, onCancel, onSubmit }) {
  const [value, setValue] = useState(Math.max(current + 1, (size.display_qty || 1) + 1));
  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4"
      role="dialog"
      data-testid={`more-dialog-${size.size_variant_id}`}
      onClick={onCancel}
    >
      <div className="bg-white rounded-lg border border-[#E2E4E0] p-5 w-full max-w-sm shadow-lg" onClick={(e) => e.stopPropagation()}>
        <div className="db-h font-semibold text-base">Looking for more?</div>
        <p className="text-sm text-[#5C665A] mt-1">
          How many are you looking to buy? We'll confirm availability after you place the request.
        </p>
        <div className="mt-3">
          <input
            type="number" min={Math.max(current + 1, 1)} value={value}
            onChange={(e) => setValue(Math.max(1, Number(e.target.value) || 0))}
            className="db-inline w-full text-center h-10 text-lg db-mono"
            data-testid={`more-dialog-input-${size.size_variant_id}`}
            autoFocus
          />
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button className="h-9 px-3 rounded-md border border-[#E2E4E0] text-sm hover:bg-[#F9F9F8]" onClick={onCancel} data-testid={`more-dialog-cancel-${size.size_variant_id}`}>Cancel</button>
          <button className="h-9 px-3 rounded-md bg-[#2D6A4F] text-white text-sm hover:bg-[#1B4332] disabled:opacity-40"
            onClick={() => onSubmit(Number(value))} disabled={!value || Number(value) < 1}
            data-testid={`more-dialog-submit-${size.size_variant_id}`}>Request {value}</button>
        </div>
      </div>
    </div>
  );
}

function NotifyBtn({ sizeVariantId }) {
  const [done, setDone] = useState(false);
  return (
    <button
      className={`inline-flex items-center gap-1 h-8 px-3 rounded-md text-xs border ${done ? "border-[#40916C] text-[#40916C] bg-[#E9F5EC]" : "border-[#E2E4E0] text-[#5C665A] hover:bg-[#F9F9F8]"}`}
      onClick={async () => { try { await api.post("/notify-me", { size_variant_id: sizeVariantId }); setDone(true); toast.success("We'll notify you when it's back."); } catch { toast.error("Failed"); } }}
      data-testid={`pd-notify-${sizeVariantId}`}
    >
      <BellRing size={12} /> {done ? "Subscribed" : "Notify me"}
    </button>
  );
}
